import React, { useState } from 'react';
import { X, Sparkles, Check } from 'lucide-react';
import { startCheckout } from '../lib/billing';
import { trackUpgradeClicked, trackUpgradeDismissed, trackCheckoutStarted, trackCheckoutFailed } from '../lib/analytics';
import {
  modalPrimaryActionClass,
  modalSecondaryActionClass,
  primaryActionDisabledClass,
  secondaryActionDisabledClass
} from '../utils/homeStyles';

/**
 * Modal component for upgrade prompts when free users hit limits
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.context - Context info (topic, limit, used)
 */
export default function UpgradeModal({ isOpen, onClose, context = {} }) {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      trackUpgradeClicked(context);
      trackCheckoutStarted();

      // Redirect to Stripe Checkout
      await startCheckout(null, window.location.origin + '/profile');
    } catch (error) {
      console.error('Failed to start checkout:', error);
      trackCheckoutFailed(error.message);
      setLoading(false);
      // Error handling is done in startCheckout
    }
  };

  const handleManageSubscription = () => {
    // Navigate to profile
    window.location.href = '/profile';
  };

  const handleClose = () => {
    trackUpgradeDismissed(context);
    onClose?.();
  };

  const { topicName, limit = 10 } = context;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#1f2530]/55 backdrop-blur-[2px] dark:bg-black/60"
        onClick={handleClose}
        aria-hidden="true"
        data-testid="backdrop"
      />

      {/* Modal content */}
      <div className="home-rounded relative z-10 w-full max-w-2xl rounded-[28px] border border-[#d2d8e2] bg-[#f1f2f5] shadow-[0_24px_60px_rgba(40,49,62,0.35)] dark:border-secondary-700 dark:bg-secondary-900">
        <div className="relative p-6 sm:p-7">
          {/* Header */}
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#d6dee9] text-[#4b586f] dark:bg-secondary-700 dark:text-secondary-200">
                <Sparkles size={20} />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#7a889f] dark:text-secondary-400">
                  Abonnement
                </p>
                <h2 id="upgrade-modal-title" className="m-0 text-2xl font-extrabold text-[#4e5563] dark:text-secondary-100">
                  Upgrade naar Pro
                </h2>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#c2ccd9] bg-white text-[#5d6675] transition-colors hover:bg-[#edf1f7] hover:text-[#3f4958] disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700 dark:hover:text-secondary-100"
              onClick={handleClose}
              aria-label="Dialoog sluiten"
              disabled={loading}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-[#d7dee8] bg-[#f1f2f5] p-4 dark:border-secondary-700 dark:bg-secondary-900">
              <p className="m-0 text-sm leading-relaxed text-[#4f596b] dark:text-secondary-200">
                {topicName
                  ? `Je zit op het Gratis-abonnement en hebt je limiet van ${limit} vragen voor ${topicName} bereikt.`
                  : 'Je zit op het Gratis-abonnement en hebt je vragenlimiet bereikt.'
                }
              </p>
            </div>

            <div className="rounded-2xl border border-[#d2d8e2] bg-[#f1f2f5] p-4 dark:border-secondary-700 dark:bg-secondary-900">
              <h3 className="mb-3 text-base font-extrabold text-[#4e5563] dark:text-secondary-100">
                Wat je krijgt met Pro
              </h3>
              <ul className="space-y-2">
                {[
                  'Onbeperkte toegang tot alle vragen',
                  'Oefen met alle onderwerpen',
                  'Volg je voortgang in de tijd',
                  'Voorrang bij ondersteuning'
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#5d6675] dark:text-secondary-300">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#d9e6d8] text-[#3f5b3f] dark:bg-green-900/30 dark:text-green-300">
                      <Check size={13} />
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-[#6f7d93] dark:text-secondary-400">
              Veilige betaling via Stripe. Je kunt op elk moment opzeggen via je profiel.
            </p>
          </div>

          {/* Actions */}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              className={`${modalPrimaryActionClass} ${loading ? primaryActionDisabledClass : ''}`}
              onClick={handleUpgrade}
              disabled={loading}
            >
              {loading ? 'Doorsturen...' : 'Upgrade naar Pro'}
            </button>
            <button
              type="button"
              className={`${modalSecondaryActionClass} ${loading ? secondaryActionDisabledClass : ''}`}
              onClick={handleManageSubscription}
              disabled={loading}
            >
              Abonnement beheren
            </button>
          </div>

          <div className="mt-3 text-center">
            <button
              type="button"
              className="text-sm text-[#5d6675] underline underline-offset-4 transition-colors hover:text-[#3f4958] disabled:cursor-not-allowed disabled:opacity-60 dark:text-secondary-400 dark:hover:text-secondary-200"
              onClick={handleClose}
              disabled={loading}
            >
              Misschien later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
